import platform from 'platform';

import { console as ReproConsole } from './utility/console';
import { PushNotificationEventProperties } from './types/push-notification.types';
import { Configuration } from './api-clients/configuration';
import { REPRO_CACHE_NAME, CACHE_STORE_FETCH_PATH } from './constants';
import { ErrorNotifier } from './error-notifier';
import { requestIdleCallback } from './utility/request-idle-callback';
import { BrowserCompatibility } from './browser-compatibility';
import { withRetry } from './utility/retry';

type SubscribeSucceeded = boolean;

interface WebPushNotificationParams {
  onSubscribeSucceed: (subscription: PushSubscriptionJSON) => void;
  onPushShow: (tracked_at: Date, properties: PushNotificationEventProperties) => void;
  onPushClick: (tracked_at: Date, properties: PushNotificationEventProperties) => void;
  onPushDataFetched: (properties: PushNotificationEventProperties) => void;
}

export class PushNotificationSubscriber {
  private vapidDecodedPublicKey: Uint8Array = new Uint8Array();
  private serviceWorkerUrl: string = '';
  private serviceWorkerScope: string = '';

  private onSubscribeSucceed: (subscription: PushSubscriptionJSON) => void;
  private onPushShow: (tracked_at: Date, properties: PushNotificationEventProperties) => void;

  private onPushClick: (tracked_at: Date, properties: PushNotificationEventProperties) => void;

  private onPushDataFetched: (properties: PushNotificationEventProperties) => void;

  constructor(params: WebPushNotificationParams) {
    this.onSubscribeSucceed = params.onSubscribeSucceed;
    this.onPushShow = params.onPushShow;
    this.onPushClick = params.onPushClick;
    this.onPushDataFetched = params.onPushDataFetched;
  }

  public setRegistrationOptions(params: NonNullable<Configuration['pushSetting']>) {
    this.vapidDecodedPublicKey = new Uint8Array(params.vapidKey);
    this.serviceWorkerUrl = params.serviceWorkerUrl;
    this.serviceWorkerScope = params.serviceWorkerScope;
  }

  public async isSubscribed(): Promise<boolean> {
    if (!this.isSubscribeAllowed) {
      return false;
    }

    // eslint-disable-next-line compat/compat
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration == null) return false;

    const subscription = await registration.pushManager.getSubscription();
    if (subscription == null) return false;

    return true;
  }

  public async subscribe(): Promise<SubscribeSucceeded> {
    if (!this.isSubscribeAllowed) {
      return false;
    }

    if ((await Notification.requestPermission()) !== 'granted') {
      ReproConsole.warn('Permission had been denied or dismissed. Subscribing to push notification is aborted.');
      return false;
    }

    const registration = await this.registerServiceWorker();

    // https://github.com/reproio/web-sdk/pull/1103 以前のように registerServiceWorker内で ServiceWorkerContainer.ready の
    // Promise を待つことはなくなったが、 registrationが取得できない = ready が resolveされなかった状況でも
    // CacheStorage 内のデータにアクセスして問題はなく、効果測定データを取り逃がさないために実行しておく
    if (registration == null) {
      this.performPeriodicCacheCheck();
      return false;
    }

    const subscription = await withRetry(async () => {
      try {
        const currentSubscription = await registration.pushManager.getSubscription();

        // すでに別の applicationServerKey を利用して PushManager に subscribe している場合、
        // Repro側で利用する applicationServerKey (= this.vapidDecodedPublicKey) を利用して subscribe しても
        // 弾かれてしまうので、一度 unsubscribe した上でふたたび subscribe し直す
        if (
          currentSubscription &&
          new Uint8Array(currentSubscription.options.applicationServerKey!).toString() !==
            this.vapidDecodedPublicKey.toString()
        ) {
          ReproConsole.warn(
            'Already subscribed to a push manager using different applicationServerKey, will attempt to unsubscribe and then subscribe using current applicationServerKey.'
          );
          await this.unsubscribe();
        }

        return registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.vapidDecodedPublicKey,
        });
      } catch (error: any) {
        this._notifyError(error);
        throw error;
      }
    });

    this.onSubscribeSucceed(subscription.toJSON());
    this.performPeriodicCacheCheck();

    return true;
  }

  private async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    try {
      return await withRetry(async () => {
        // eslint-disable-next-line compat/compat
        return navigator.serviceWorker.register(this.serviceWorkerUrl, {
          scope: this.serviceWorkerScope,
        });
      });
    } catch (error: any) {
      this._notifyError(error);
    }
    return null;
  }

  public async unsubscribe(): Promise<void> {
    if (
      !BrowserCompatibility.isPushNotificationSupported({
        name: platform.name,
        userAgent: navigator.userAgent,
      })
    )
      return;

    // eslint-disable-next-line compat/compat
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration == null) return;

    const subscription = await registration.pushManager.getSubscription();
    if (subscription == null) return;

    subscription.unsubscribe().catch((error) => {
      this._notifyError(error);
    });
  }

  public get isSubscribeAllowed(): boolean {
    if (
      !BrowserCompatibility.isPushNotificationSupported({
        name: platform.name,
        userAgent: navigator.userAgent,
      })
    ) {
      ReproConsole.warn('Push Notification is not supported');
      return false;
    }

    if (PushNotificationSubscriber.isPermissionDenied) {
      ReproConsole.warn('Permission has already been denied');
      return false;
    }

    if (this.serviceWorkerUrl === null || this.vapidDecodedPublicKey === null) {
      ReproConsole.warn('Push Notification is not activated for this project');
      return false;
    }

    if (window.location.origin !== new URL(this.serviceWorkerUrl).origin) {
      ReproConsole.warn(
        `The origin of the provided Service Worker ('${this.serviceWorkerUrl}') does not match the current origin ('${window.location.origin}').`
      );
      return false;
    }

    return true;
  }

  /*
    Web Push に関する効果測定データの収集と送信は次のようなフローで実現されている

    1. Web Pushが PushManager（Chromeの場合はFCM）からクライアントに送られる
      - ref. https://developer.mozilla.org/en-US/docs/Web/API/PushManager
    2. ページのコントローラとなっているServiceWorkerの push イベントハンドラが実行される
      - Web SDK的には https://github.com/reproio/web-sdk/blob/fbc7084/service-worker.ts#L43-L51
    3. push イベントハンドラの中で受け取ったWeb Pushに関する情報を CacheStorage に保存する
      - 各パラメータは CacheStorage に保存する Response オブジェクトのURLにクエリパラメータとしてエンコードされる
    4. Web SDK本体の側から定期的に CacheStorage の中身をチェックし、Web Pushの効果測定データがあればそれをイベントとして組み立てて eventChunk に追加する
      - eventChunk として送る処理の実体は WebPushNotificationParams として受け取っている関数一式が該当する
      - ServiceWorker を登録した scope に一致しない場合は ServiceWorkerRegistration が取得できないため、
        subscribeとは別に呼び出し側から performPeriodicCacheCheck() を実行する
  */
  private async cacheFetch(): Promise<void> {
    const cache = await caches.open(REPRO_CACHE_NAME);
    const responses = await cache.matchAll(CACHE_STORE_FETCH_PATH, { ignoreSearch: true });
    if (responses == null) return;
    const cachedUrls = await Promise.all(responses.map((res) => res.text()));

    cachedUrls.forEach((cachedUrl) => {
      const url = new URL(cachedUrl, window.location.origin);
      // iOS 9 URL().searchParams がないため、polyfillが入ってる URLSearchParams を使う
      const params = new URLSearchParams(url.search);

      const action = params.get('action');
      const internal_event_gid = params.get('internal_event_gid');
      const internal_campaign_gid = params.get('internal_campaign_gid');
      const tracked_at = params.get('tracked_at');

      if (action == null || internal_event_gid == null || internal_campaign_gid == null || tracked_at == null) return;

      const properties = { internal_event_gid, internal_campaign_gid };

      if (action === 'show') {
        this.onPushShow(new Date(parseInt(tracked_at, 10)), properties);
      } else if (action === 'click') {
        this.onPushClick(new Date(parseInt(tracked_at, 10)), properties);
      }
      return this.onPushDataFetched(properties);
    });

    cache.delete(CACHE_STORE_FETCH_PATH, { ignoreSearch: true });
  }

  private performPeriodicCacheCheck(proc: () => Promise<void> = () => this.cacheFetch()): void {
    if (!this.isSubscribeAllowed) {
      return;
    }

    const interval = 10000; // 10 seconds

    requestIdleCallback(() => {
      // オフラインの状態ではCache FetchしてもeventChunkとして送れないので
      // オンラインになるまでCache Fetchはせずポーリングする
      if (!navigator.onLine) {
        window.setTimeout(() => this.performPeriodicCacheCheck(proc), interval);
        return;
      }
      // 最初に Cache Fetch を実行後は一定間隔で繰り返す
      proc();
      window.setTimeout(() => this.performPeriodicCacheCheck(proc), interval);
    });
  }

  private _notifyError(error: Error): void {
    ReproConsole.error({ error });
    ErrorNotifier.notify({
      error,
      extraPayload: {
        serviceWorkerUrl: this.serviceWorkerUrl,
        serviceWorkerScope: this.serviceWorkerScope,
        vapidDecodedPublicKey: `${this.vapidDecodedPublicKey}`,
      },
    });
  }

  public static get isPermissionGranted(): boolean {
    // eslint-disable-next-line compat/compat
    return 'Notification' in window && Notification.permission === 'granted';
  }

  public static get isPermissionDenied(): boolean {
    // eslint-disable-next-line compat/compat
    return 'Notification' in window && Notification.permission === 'denied';
  }
}
